import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Gift } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/dashboard';
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    username: "",
    birthday: ""
  });
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!acceptedPrivacy) {
      toast.error("Tu dois accepter la politique de confidentialité pour t'inscrire");
      return;
    }
    
    setLoading(true);

    try {
      // Combine firstName and lastName for backend
      const registrationData = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        username: formData.username,
        email: formData.email,
        password: formData.password,
        birthday: formData.birthday
      };
      
      await axios.post(`${API}/auth/register`, registrationData);
      toast.success("Inscription réussie ! Connectez-vous maintenant.");
      navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'inscription");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const googleRedirect = window.location.origin + redirectUrl;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(googleRedirect)}`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Gift className="w-16 h-16 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-3xl font-bold">Créer un compte</h1>
            <p className="text-muted-foreground mt-2">Devenez la personne la plus facile à gâter du monde.</p>
          </div>

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle data-testid="register-title" className="text-center">Inscription</CardTitle>
              <CardDescription className="text-center">Remplissez vos informations pour créer un compte</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                data-testid="google-register-btn"
                type="button"
                variant="outline"
              className="w-full rounded-full py-6 font-bold mb-6"
              onClick={handleGoogleLogin}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              S'inscrire avec Google
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t"></span>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Ou avec email</span>
              </div>
            </div>

            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-center block">Prénom</Label>
                  <Input
                    data-testid="firstname-input"
                    id="firstName"
                    name="firstName"
                    type="text"
                    placeholder="Jean"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-center block">Nom</Label>
                  <Input
                    data-testid="lastname-input"
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Dupont"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="username" className="text-center block">Nom d'utilisateur</Label>
                <Input
                  data-testid="username-input"
                  id="username"
                  name="username"
                  type="text"
                  placeholder="jeandupont"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1 text-center">
                  Votre lien sera : /wishlist/{formData.username || "votre-username"}
                </p>
              </div>

              <div>
                <Label htmlFor="birthday" className="text-center block">Date d'anniversaire</Label>
                <Input
                  data-testid="birthday-input"
                  id="birthday"
                  name="birthday"
                  type="date"
                  value={formData.birthday}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="email" className="text-center block">Email</Label>
                <Input
                  data-testid="email-input"
                  id="email"
                  name="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="password" className="text-center block">Mot de passe</Label>
                <Input
                  data-testid="password-input"
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>

              <div className="flex items-start gap-3 py-2">
                <Checkbox 
                  id="privacy"
                  data-testid="privacy-checkbox"
                  checked={acceptedPrivacy}
                  onCheckedChange={setAcceptedPrivacy}
                  className="mt-0.5"
                />
                <Label htmlFor="privacy" className="text-sm leading-relaxed cursor-pointer">
                  J'accepte la{' '}
                  <Link to="/privacy" target="_blank" className="text-primary hover:underline">
                    Politique de confidentialité
                  </Link>
                </Label>
              </div>

              <Button 
                data-testid="register-submit-btn"
                type="submit" 
                className="w-full rounded-full py-6 font-bold" 
                disabled={loading || !acceptedPrivacy}
              >
                {loading ? "Création..." : "Créer mon compte"}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-primary hover:underline font-bold">
                Se connecter
              </Link>
            </p>
          </CardContent>
        </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}